import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Keyboard, X, MousePointer2, Move, Layers, Eye, Play, HelpCircle, MapPin } from "lucide-react";

const STORAGE_KEY = "blueprintai_shortcuts_seen";

const SHORTCUTS = [
  { keys: ["Drag"],          label: "Orbit camera",       icon: MousePointer2 },
  { keys: ["Scroll"],        label: "Zoom in / out",      icon: Move },
  { keys: ["Right-click"],   label: "Pan camera",         icon: Move },
  { keys: ["X"],             label: "Toggle X-Ray Walls", icon: Eye },
  { keys: ["1", "2", "3"],   label: "Section-cut floor",  icon: Layers },
  { keys: ["0"],             label: "Show all floors",    icon: Layers },
  { keys: ["Space"],         label: "Play cinematic tour", icon: Play },
  { keys: ["?"],             label: "Open this panel",    icon: HelpCircle },
];

/**
 * Keyboard shortcut hints overlay + global key handler.
 *
 * Mounting behaviour:
 *   - Pops automatically on the first visit per device (gated by localStorage).
 *   - Re-summons on the `?` key (Shift + /).
 *   - Dismissable by Escape, click on the backdrop, or the X button.
 *
 * Action wiring (the parent passes callbacks):
 *   - onToggleXRay         — invoked on `x` / `X`
 *   - onSelectFloor(level) — invoked on `1`/`2`/`3` (level=that digit) and `0` (level=null → all floors)
 *   - onPlayTour           — invoked on Space
 *
 * The handler ignores events that originate inside text inputs / textareas
 * so typing "x" in the chat doesn't toggle X-Ray.
 */
export default function ShortcutsOverlay({
  onToggleXRay,
  onSelectFloor,
  onPlayTour,
}) {
  const [open, setOpen] = useState(false);

  // V2.0.0 — Auto-open once per device, BUT cancel the timer if the
  // user starts interacting with the canvas before it fires. Previously
  // the 1.5s timer would unconditionally pop the modal and its
  // inset-0 z-50 backdrop swallowed every door/window-overlay click for
  // first-time users — they saw nothing happen when they tried to click
  // an AI-placed door (a confusing dead-end). Cancelling on the first
  // pointerdown/keydown keeps the modal discoverable (still pops if the
  // user just stares at the canvas) without blocking real edits.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return undefined;
    } catch (_) {
      // localStorage may be unavailable (private mode, embedded view, etc.)
      return undefined;
    }
    const cancel = () => {
      cancelled = true;
      try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch (err) { console.debug("[Shortcuts] localStorage write failed (cancel):", err); }
      window.removeEventListener("pointerdown", cancel, true);
      window.removeEventListener("keydown", cancel, true);
    };
    window.addEventListener("pointerdown", cancel, true);
    window.addEventListener("keydown", cancel, true);
    const t = setTimeout(() => {
      if (cancelled) return;
      setOpen(true);
      try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch (err) { console.debug("[Shortcuts] localStorage write failed (open):", err); }
      window.removeEventListener("pointerdown", cancel, true);
      window.removeEventListener("keydown", cancel, true);
    }, 1500);
    return () => {
      clearTimeout(t);
      window.removeEventListener("pointerdown", cancel, true);
      window.removeEventListener("keydown", cancel, true);
    };
  }, []);

  // Global key listener for shortcuts + ? to summon.
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      // Bail if the event originates from a text input / textarea / contenteditable.
      if (t && t.matches && (
        t.matches("input, textarea, [contenteditable=true]") ||
        t.closest?.("input, textarea, [contenteditable=true]")
      )) return;

      // Open shortcuts on `?` (typically Shift + /).
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      // Close on Escape when open.
      if (e.key === "Escape" && open) {
        setOpen(false);
        return;
      }
      // Action shortcuts only fire when the panel is closed (so opening
      // the panel doesn't immediately consume a key).
      if (open) return;

      const key = e.key;
      if (key === "x" || key === "X") {
        e.preventDefault();
        onToggleXRay?.();
      } else if (key === "1" || key === "2" || key === "3") {
        e.preventDefault();
        onSelectFloor?.(parseInt(key, 10));
      } else if (key === "0") {
        e.preventDefault();
        onSelectFloor?.(null);
      } else if (key === " " || e.code === "Space") {
        e.preventDefault();
        onPlayTour?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onToggleXRay, onSelectFloor, onPlayTour]);

  return (
    <>
      {/* Floating help launcher — bottom-right corner, hidden on mobile (FAB
          area). Always visible on lg+ so the panel is rediscoverable. */}
      <button
        type="button"
        data-testid="shortcuts-launcher"
        onClick={() => setOpen(true)}
        title="Keyboard shortcuts (?)"
        aria-label="Open keyboard shortcuts"
        className="hidden lg:grid place-items-center fixed bottom-20 right-5 lg:right-[19rem] z-50 w-10 h-10 rounded-full border border-zinc-700 bg-zinc-950/85 backdrop-blur text-zinc-400 hover:text-brass-300 hover:border-brass-500 transition-colors"
      >
        <Keyboard className="w-4 h-4" />
      </button>

      {open && (
        <div
          data-testid="shortcuts-overlay"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/80 backdrop-blur-sm p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-md border border-brass-500/50 bg-zinc-950 shadow-[0_24px_72px_rgba(0,0,0,0.75)]">
            <header className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <div className="flex items-center gap-2.5">
                <Keyboard className="w-4 h-4 text-brass-400" />
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-brass-500/80">
                    // Blueprint AI
                  </div>
                  <h2 className="font-display text-lg text-zinc-50 leading-tight">
                    Keyboard shortcuts
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="shortcuts-close"
                aria-label="Close shortcuts"
                className="text-zinc-400 hover:text-zinc-50 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="px-5 py-4 space-y-2">
              {SHORTCUTS.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.label}
                    className="flex items-center justify-between gap-3 py-1.5"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon className="w-3.5 h-3.5 text-brass-400/70 flex-shrink-0" />
                      <span className="font-body text-sm text-zinc-200 truncate">
                        {s.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {s.keys.map((k, i) => (
                        <React.Fragment key={k}>
                          {i > 0 && (
                            <span className="font-mono text-[10px] text-zinc-600">/</span>
                          )}
                          <kbd className="inline-grid place-items-center min-w-[1.75rem] h-6 px-1.5 border border-zinc-700 bg-zinc-900 font-mono text-[10px] uppercase tracking-widest text-brass-300">
                            {k}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <footer className="px-5 py-3 border-t border-zinc-800 bg-zinc-950/60 space-y-2">
              {/* V2.0.15 — Public Local Code Finder discoverability from the
                  Editor's most-opened help surface. */}
              <Link
                to="/local-code-finder"
                target="_blank"
                rel="noreferrer"
                data-testid="shortcuts-local-code-finder-link"
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-brass-300 hover:text-brass-200 transition-colors"
              >
                <MapPin className="w-3 h-3" />
                Open Local Code Finder →
              </Link>
              <p className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 leading-relaxed">
                // Press <kbd className="px-1 border border-zinc-700 text-brass-300">?</kbd> anytime to reopen this panel.
              </p>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
